import { Grid2 as Grid, Typography, Chip, Box } from '@mui/material'
import { ecgTypes } from 'pages/Diagnostics/constants/diagnostic_types'
import { IExamResponse } from 'types/exam/types'
import { ECGDiagnosticCode } from 'pages/Diagnostics/types'
import { useLeadHighlight } from './hooks/useLeadHighlight'
import { ECGDiagnosticCard } from './components/ECGDiagnosticCard'
import {
  ErrorOutlineOutlined,
  CheckCircleOutline,
  NotesOutlined,
  InfoOutlined,
  CheckCircleOutlined
} from '@mui/icons-material'
import { getECGResultConfig } from 'pages/Diagnostics/constants'

interface IECGDiagnosisProps {
  examData: IExamResponse
}

const ECGDiagnostics = ({ examData }: IECGDiagnosisProps) => {
  const { highlightLeads } = useLeadHighlight()

  // Filtrar resultados normais (não patológicos)
  const normalFindings = examData.exam_results.filter(result => {
    return result.is_pathological === 0
  })

  // Filtrar emergências (patológicos com emergency > 0)
  const emergencies = examData.exam_results
    .filter(result => {
      const diagnosisData = ecgTypes[result.result_type as ECGDiagnosticCode]
      return diagnosisData?.emergency > 0 && result.is_pathological !== 0
    })
    // Sort emergencies by severity
    .sort(
      (a, b) =>
        ecgTypes[b.result_type as ECGDiagnosticCode].emergency -
        ecgTypes[a.result_type as ECGDiagnosticCode].emergency
    )

  // Filtrar achados clínicos (patológicos com emergency === 0)
  const clinicalFindings = examData.exam_results.filter(result => {
    const diagnosisData = ecgTypes[result.result_type as ECGDiagnosticCode]
    return diagnosisData?.emergency === 0 && result.is_pathological !== 0
  })

  return (
    <Grid container mb={3}>
      <Grid size={12}>
        {/* Seção de Achados Normais - Badge Format */}
        {normalFindings.length > 0 && (
          <>
            <Grid>
              <Typography
                variant='h6'
                sx={{
                  mb: 1,
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <CheckCircleOutlined /> Achados Normais
              </Typography>
            </Grid>
            <Box
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, mb: 2 }}
            >
              {normalFindings.map((result, index) => {
                const diagnosisData =
                  ecgTypes[result.result_type as ECGDiagnosticCode]

                if (!diagnosisData) return null
                const resultConfig = getECGResultConfig(result.result_value)

                return (
                  <Chip
                    key={`${result.result_type}-${index}`}
                    icon={<CheckCircleOutline />}
                    label={`${diagnosisData.name} (Nível de Confiança: ${resultConfig.risk})`}
                    onClick={() => highlightLeads(diagnosisData.leads)}
                    color='success'
                    variant='outlined'
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: theme =>
                          theme.palette.success.main + '20'
                      }
                    }}
                  />
                )
              })}
            </Box>
          </>
        )}

        {/* Seção de Emergências */}
        {emergencies.length > 0 && (
          <>
            <Grid>
              <Typography
                variant='h6'
                color='error'
                sx={{
                  mb: 1,
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <ErrorOutlineOutlined /> Casos de Emergência
              </Typography>
            </Grid>
            <Grid container spacing={2}>
              {emergencies.map((result, index) => {
                const diagnosisData =
                  ecgTypes[result.result_type as ECGDiagnosticCode]

                if (!diagnosisData) return null

                return (
                  <Grid
                    key={`${result.result_type}-${index}`}
                    size={{ xs: 12, lg: 6 }}
                  >
                    <ECGDiagnosticCard
                      result={result}
                      diagnosisData={diagnosisData}
                      onCardClick={highlightLeads}
                      isEmergency={true}
                      variant='emergency'
                    />
                  </Grid>
                )
              })}
            </Grid>
          </>
        )}
        {/* Seção de Achados Clínicos */}
        {clinicalFindings.length > 0 && (
          <>
            <Grid>
              <Typography
                variant='h6'
                sx={{
                  mb: 1,
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <InfoOutlined /> Achados clínicos
              </Typography>
            </Grid>
            <Grid container spacing={2}>
              {clinicalFindings.map((result, index) => {
                const diagnosisData =
                  ecgTypes[result.result_type as ECGDiagnosticCode]

                if (!diagnosisData) return null

                return (
                  <Grid
                    key={`${result.result_type}-${index}`}
                    size={{ xs: 12, lg: 6 }}
                  >
                    <ECGDiagnosticCard
                      result={result}
                      diagnosisData={diagnosisData}
                      onCardClick={highlightLeads}
                    />
                  </Grid>
                )
              })}
            </Grid>
          </>
        )}
      </Grid>
    </Grid>
  )
}

export default ECGDiagnostics
